import React, { useState, useEffect, useCallback } from 'react';
import { UserCircle, Mail, Phone, Calendar, Sparkles, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subscription_plan: 'glow_plan'
  });

  const plans = [
    { value: 'glow_plan', label: 'Glow Plan - $55/mo', emoji: '✨' },
    { value: 'acrylic_plan', label: 'Acrylic Plan - $75/mo', emoji: '💎' },
    { value: null, label: 'No Subscription', emoji: '💅' }
  ];

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clients`);
      setClients(res.data);
      setLoading(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching clients:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleAddClient = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/clients`, formData);
      setShowAddModal(false);
      setFormData({ name: '', email: '', phone: '', subscription_plan: 'glow_plan' });
      fetchClients();
      alert('Client added successfully!');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error adding client:', error);
      alert('Error adding client');
    }
  };

  const getPlanDetails = (plan) => {
    const planMap = {
      glow_plan: { name: 'Glow Plan', color: 'from-amber-400 to-amber-600', emoji: '✨' },
      acrylic_plan: { name: 'Acrylic Plan', color: 'from-purple-400 to-purple-600', emoji: '💎' },
      null: { name: 'Pay Per Visit', color: 'from-gray-400 to-gray-600', emoji: '💅' }
    };
    return planMap[plan] || planMap[null];
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/10 text-green-600 border-green-500/20',
      inactive: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
      cancelled: 'bg-red-500/10 text-red-600 border-red-500/20'
    };
    return colors[status] || colors.active;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-1">Manage your fabulous clients ✨</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-amber-500 to-amber-600"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clients</p>
                <p className="text-3xl font-bold mt-1">{clients.length}</p>
              </div>
              <UserCircle className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Subscribers</p>
                <p className="text-3xl font-bold mt-1">
                  {clients.filter(c => c.subscription_status === 'active').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Glow Plan</p>
                <p className="text-3xl font-bold mt-1">
                  {clients.filter(c => c.subscription_plan === 'glow_plan').length}
                </p>
              </div>
              <span className="text-3xl">✨</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Acrylic Plan</p>
                <p className="text-3xl font-bold mt-1">
                  {clients.filter(c => c.subscription_plan === 'acrylic_plan').length}
                </p>
              </div>
              <span className="text-3xl">💎</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients List */}
      <Card>
        <CardHeader>
          <CardTitle>All Clients</CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="text-center py-12">
              <UserCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No clients yet</p>
              <p className="text-muted-foreground mb-4">Add your first client to get started</p>
              <Button onClick={() => setShowAddModal(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map((client) => {
                const planDetails = getPlanDetails(client.subscription_plan);
                return (
                  <div
                    key={client.id}
                    className="flex items-center justify-between p-4 bg-accent/30 rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${planDetails.color} flex items-center justify-center text-white text-xl font-bold`}>
                        {client.name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{client.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(client.subscription_status)}`}>
                            {client.subscription_status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {client.email}
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {client.phone}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {client.total_appointments} appointments
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{planDetails.emoji}</span>
                        <div>
                          <p className="text-sm font-medium">{planDetails.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Since {new Date(client.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddClient} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Sarah Johnson"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="sarah@email.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="904-555-0123"
                required
              />
            </div>

            <div>
              <Label htmlFor="plan">Subscription Plan</Label>
              <select
                id="plan"
                value={formData.subscription_plan || ''}
                onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value || null })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
              >
                {plans.map(plan => (
                  <option key={plan.value || 'none'} value={plan.value || ''}>
                    {plan.emoji} {plan.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600">
                Add Client
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;